import NumberInput from "components/app/function/NumberInput";
import TextInput from "components/app/function/TextInput";
import { Button } from "components/ui/button";
import { Label } from "components/ui/label";
import { ScrollArea, ScrollBar } from "components/ui/scroll-area";
import { AppLog } from "services";
import { useAppSelector } from "store/store";
import { RequestApi, RouteApi, StatusMsg } from "utils";
import { Sheet, SheetClose, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle, SheetTrigger } from "components/app/sheet/sheet";
import { LoaderCircle, UserRoundPen, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Textarea } from "components/ui/textarea";
import GoogleMapEdit from "components/app/google-map/GoogleMapEdit";
import useProfileStore from "store/useProfile";

export default function DailogEditPageProfile() {
  const [getUser, setGetUser] = useState<any>();
  const { token } = useAppSelector((state) => state.auth);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { userPageInfomation, fetchGetPageInformation } = useProfileStore();

  const submitProfileApi = async () => {
    setIsLoading(true);
    try {
      const url = `${RouteApi.submitUpdateUserPage}token=${token}`;
      const data = { page_id: getUser.id, page: getUser };
      const response = await RequestApi(url, { data });
      if (response.status === "success") {
        setIsDialogOpen(false);
        setIsLoading(false);
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      AppLog.show({
        message: `[submitProfileApi]`,
        status: StatusMsg.error,
        error,
      });
    }
  };
  const handleSave = () => {
    submitProfileApi();
  };

  useEffect(() => {
    setGetUser(userPageInfomation);
  }, [userPageInfomation]);

  useEffect(() => {
    if (isDialogOpen) {
      fetchGetPageInformation();
    }
  }, [isDialogOpen]);

  return (
    <Sheet open={isDialogOpen}>
      <SheetTrigger
        onClick={() => {
          setIsDialogOpen(true);
        }}
        asChild
      >
        <UserRoundPen className="p-1 bg-gray-50 dark:bg-accent dark:hover:bg-slate-900 hover:bg-accent hover:text-accent-foreground w-9 h-9 rounded-full border cursor-pointer" />
      </SheetTrigger>
      <SheetContent isShowBtnXClose={false} className="p-0 pb-2">
        <div className="flex flex-col items-center justify-between h-full">
          <SheetHeader className="mx-4">
            <div
              className="absolute flex items-center justify-center right-3 top-1 z-40 cursor-pointer rounded-full hover:bg-accent hover:text-accent-foreground w-[36px] h-[36px] p-2"
              onClick={() => {
                setIsDialogOpen(false);
              }}
            >
              <X />
            </div>

            <SheetTitle className="text-start w-full ">Edit Page Detail</SheetTitle>
            <SheetHeader className=" mt-2">
              <SheetDescription>Modify your profile here, then click "Save" when you’re finished</SheetDescription>
            </SheetHeader>
          </SheetHeader>
          <ScrollArea className="mt-2 w-full h-full">
            <div className="space-y-2 mx-4 pb-6">
              <div className="space-y-1">
                <Label htmlFor="page_name" className="">
                  Page Name
                </Label>
                <TextInput
                  onChange={(event) => {
                    const tmpUser = {
                      ...getUser,
                    };
                    tmpUser.name = event.target.value;
                    setGetUser(tmpUser);
                  }}
                  id="page_name"
                  value={getUser?.name}
                  className="text-[14px]"
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="bio" className="">
                  Bio
                </Label>
                <Textarea
                  onChange={(event) => {
                    const tmpUser = {
                      ...getUser,
                    };
                    tmpUser.bio = event.target.value;
                    setGetUser(tmpUser);
                  }}
                  id="bio"
                  value={getUser?.bio}
                  className="text-[14px]"
                  maxLength={255}
                />
                <div className="text-right text-xs font-semibold">
                  {getUser?.bio?.length > 0 ? getUser?.bio?.length : 0}
                  /255
                </div>
              </div>
              <div className="space-y-1">
                <Label htmlFor="description" className="">
                  Description
                </Label>
                <Textarea
                  onChange={(event) => {
                    const tmpUser = {
                      ...getUser,
                    };
                    tmpUser.description = event.target.value;
                    setGetUser(tmpUser);
                  }}
                  id="description"
                  value={getUser?.description}
                  className="text-[14px]"
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="hashtag" className="">
                  Hashtag
                </Label>
                <TextInput
                  onChange={(event) => {
                    const tmpUser = {
                      ...getUser,
                    };
                    tmpUser.hashtag = event.target.value;
                    setGetUser(tmpUser);
                  }}
                  id="hashtag"
                  value={getUser?.hashtag}
                  className="text-[14px]"
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="phone_number" className="">
                  Phone Number
                </Label>
                <div className="relative">
                  <NumberInput
                    onChange={(event) => {
                      const tmpUser = {
                        ...getUser,
                      };
                      tmpUser.phone_no = event.target.value;
                      setGetUser(tmpUser);
                    }}
                    id="phone_number"
                    value={getUser?.phone_no}
                    className="text-[14px] w-full rounded-md px-3 py-2 pr-16"
                  />
                  {getUser?.phone_no_verified === "Yes" && <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-green-600 font-medium">Verified</span>}
                </div>
              </div>

              <div className="space-y-1">
                <Label htmlFor="email" className="">
                  Email
                </Label>
                <div className="relative">
                  <TextInput
                    onChange={(event) => {
                      const tmpUser = {
                        ...getUser,
                      };
                      tmpUser.user_email = event.target.value;
                      setGetUser(tmpUser);
                    }}
                    id="email"
                    value={getUser?.user_email}
                    className="text-[14px] w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <Label htmlFor="facebook_link" className="">
                  Facebook Link
                </Label>
                <div className="relative">
                  <TextInput
                    onChange={(event) => {
                      const tmpUser = {
                        ...getUser,
                      };
                      tmpUser.facebook_link = event.target.value;
                      setGetUser(tmpUser);
                    }}
                    id="facebook_link"
                    value={getUser?.facebook_link}
                    className="text-[14px] w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <Label htmlFor="telegram_link" className="">
                  Telegram Link
                </Label>
                <div className="relative">
                  <TextInput
                    onChange={(event) => {
                      const tmpUser = {
                        ...getUser,
                      };
                      tmpUser.telegram_link = event.target.value;
                      setGetUser(tmpUser);
                    }}
                    id="telegram_link"
                    value={getUser?.telegram_link}
                    className="text-[14px] w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <Label htmlFor="tiktok_link" className="">
                  Tiktok Link
                </Label>
                <div className="relative">
                  <TextInput
                    onChange={(event) => {
                      const tmpUser = {
                        ...getUser,
                      };
                      tmpUser.tiktok_link = event.target.value;
                      setGetUser(tmpUser);
                    }}
                    id="tiktok_link"
                    value={getUser?.tiktok_link}
                    className="text-[14px] w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <Label htmlFor="youtube_link" className="">
                  Youtube Link
                </Label>
                <div className="relative">
                  <TextInput
                    onChange={(event) => {
                      const tmpUser = {
                        ...getUser,
                      };
                      tmpUser.youtube_link = event.target.value;
                      setGetUser(tmpUser);
                    }}
                    id="email"
                    value={getUser?.youtube_link}
                    className="text-[14px] w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <Label htmlFor="google_map_link" className="">
                  Google Map Link
                </Label>
                <div className="relative">
                  <TextInput
                    onChange={(event) => {
                      const tmpUser = {
                        ...getUser,
                      };
                      tmpUser.google_map_link = event.target.value;
                      setGetUser(tmpUser);
                    }}
                    id="email"
                    value={getUser?.google_map_link}
                    className="text-[14px] w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <Label htmlFor="address" className="">
                  Address
                </Label>
                <TextInput
                  onChange={(event) => {
                    const tmpUser = {
                      ...getUser,
                    };
                    tmpUser.current_address = event.target.value;
                    setGetUser(tmpUser);
                  }}
                  id="address"
                  value={getUser?.current_address}
                  className="text-[14px]"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="city" className="">
                  City
                </Label>
                <TextInput id="city" disabled value={getUser?.current_city} className="text-[14px]" />
              </div>

              <div className="space-y-1">
                <Label htmlFor="country" className="">
                  Country
                </Label>
                <TextInput id="country" disabled value={getUser?.current_country} className="text-[14px]" />
              </div>

              <div className="space-y-1">
                <Label htmlFor="location" className="">
                  Location
                </Label>
                <div className="h-[400px] w-full">
                  <GoogleMapEdit
                    latitude={getUser?.current_latitude}
                    longitude={getUser?.current_longitude}
                    onValueChange={(value) => {
                      setGetUser((prevUser: any) => ({
                        ...prevUser,
                        current_latitude: value.lat,
                        current_longitude: value.lng,
                      }));
                    }}
                    getLocationDetail={(value) => {
                      setGetUser((prevUser: any) => ({
                        ...prevUser,
                        current_city: value.city,
                        current_country: value.country,
                      }));
                    }}
                  />
                </div>
              </div>
            </div>
            <ScrollBar orientation="vertical" />
          </ScrollArea>
          <SheetFooter className="w-full px-4">
            <SheetClose onClick={() => handleSave()} className="w-full" asChild>
              <Button className="w-full bg-destructive text-white bg-blue-600 hover:bg-blue-500">Save</Button>
            </SheetClose>
          </SheetFooter>
          {isLoading && (
            <div className="w-full h-full bg-black bg-opacity-70 flex justify-center absolute items-center ">
              <LoaderCircle size={30} className="animate-spin text-yellow-400" />
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
